import React, { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import {
  buildPipelineFlowStagesFromCounts,
  getTeamWeeklyCreditRunway,
  getStatusCountsFromAggregate,
} from '../../utils/campaignMetrics';
import { PipelineChart, type PipelineChartStage } from '../common/PipelineChart';
import { PipelineStatusModal } from './PipelineStatusModal';
import type { CampaignMetadata } from '../../types';
import type { TeamMemberData } from '../../types/n8nResponses';

/** Lead-pipeline chart presentation. Persisted per browser so the chosen
 *  visualization survives reloads (same pattern as campaignViewMode). */
type PipelineViewMode = 'bars' | 'funnel';
const PIPELINE_VIEW_KEY = 'teamPipelineView';
const loadPipelineView = (): PipelineViewMode => {
  try {
    return localStorage.getItem(PIPELINE_VIEW_KEY) === 'funnel' ? 'funnel' : 'bars';
  } catch {
    return 'bars';
  }
};

// ASSIGNED-legend density rules (Manuel): a big roster must not stretch the
// hero row vertically. Above COMPACT threshold the legend switches to a
// 2-column grid (grows rightward) and the donut column cedes no more — the
// PIPELINE column gives up width instead. Hard cap: past MAX entries the
// tail collapses into one aggregated "+N more" row so height stays bounded.
const LEGEND_COMPACT_THRESHOLD = 10;
const LEGEND_MAX_ENTRIES = 30;

export const TeamCampaignOverview: React.FC<{
  campaigns: CampaignMetadata[]; active: number; unassigned: number;
  teamMembers: Array<{ name: string; email: string; color: string; count: number }>;
  onAll: () => void; onActive: () => void; onUnassigned: () => void;
  /** Team's current AI-credit balance — powers the weekly runway pill. */
  availableCredits?: number | null;
  /** Opens the Buy-Credits modal — surfaced inside the pill when coverage runs low. */
  onAddCredits?: () => void;
  /** Authenticated (or impersonated) user email — fetches the per-status
   *  lead lists when a pipeline stage is clicked. */
  userEmail: string;
  /** Campaign ID → seller display name (registry-resolved by the board) —
   *  shown in the pipeline drill-down's campaign headers. */
  sellerLabels?: Record<string, string | null>;
}> = ({ campaigns, active, unassigned, teamMembers, onAll, onActive, onUnassigned, availableCredits, onAddCredits, userEmail, sellerLabels }) => {
  const stages = useMemo(() => {
    // SAME canonical builder the main Campaigns page uses — colors come from
    // STATUS_MAP (design-system source of truth) instead of ad-hoc hexes.
    // The graph aggregates the CURRENT SELECTION (Manuel): every campaign the
    // board passes in counts, so picking Paused or Archived shows THOSE
    // campaigns' pipeline too. The live-only rule lives ONLY in the weekly
    // pill below — that one measures enrichment capacity (spending), not
    // lead data.
    const totals = campaigns.reduce((accumulator, campaign) => {
      const counts = getStatusCountsFromAggregate(campaign.leads_by_status);
      return {
        new_lead: accumulator.new_lead + counts.new_lead,
        enriching: accumulator.enriching + counts.enriching,
        enriched: accumulator.enriched + counts.enriched_cumulative,
        outreaching: accumulator.outreaching + counts.outreaching,
        outreached: accumulator.outreached + counts.outreached,
        engaged: accumulator.engaged + counts.engaged,
        proposal: accumulator.proposal + counts.proposal,
        converted: accumulator.converted + counts.converted,
      };
    }, { new_lead: 0, enriching: 0, enriched: 0, outreaching: 0, outreached: 0, engaged: 0, proposal: 0, converted: 0 });
    return buildPipelineFlowStagesFromCounts({ ...totals, enriched_cumulative: totals.enriched, discarded: 0, total_leads: 0 });
  }, [campaigns]);
  const total = stages.reduce((sum, stage) => sum + stage.value, 0);
  /** Pipeline stage currently drilled into (drives the leads modal). */
  const [activeStage, setActiveStage] = useState<PipelineChartStage | null>(null);
  // Weekly investment + credit runway in ONE call — the TOTAL WEEKLY LIMIT
  // (live campaigns only) and how many weeks the team's credits cover at the
  // ENRICH floor burn. Rule + math live in utils/campaignMetrics.ts.
  const runway = useMemo(() => getTeamWeeklyCreditRunway(campaigns, availableCredits), [campaigns, availableCredits]);

  // ASSIGNED DONUT — campaigns per owner + the unassigned slice. Members with
  // zero assigned campaigns are already excluded (member.count <= 0), so the
  // legend only ever lists contributors.
  const donutSlices = useMemo(() => {
    const totalCampaigns = Math.max(1, campaigns.length);
    const palette = ['#67E8F9', '#A78BFA', '#FBBF24', '#F97316', '#EC4899', '#34D399', '#94A3B8', '#60A5FA'];
    const slices: Array<{ label: string; count: number; color: string; fraction: number }> = [];
    teamMembers.forEach((member, index) => {
      if (member.count <= 0) return;
      const fraction = member.count / totalCampaigns;
      slices.push({ label: member.name, count: member.count, color: member.color || palette[index % palette.length], fraction });
    });
    if (unassigned > 0) {
      slices.push({ label: 'Unassigned', count: unassigned, color: 'rgba(255,255,255,0.14)', fraction: unassigned / totalCampaigns });
    }
    // Hard cap: collapse the tail into ONE aggregated slice so the legend
    // (and the donut's arc count) stays bounded on huge rosters. 29 named +
    // 1 "+N more" = 30 entries max.
    if (slices.length > LEGEND_MAX_ENTRIES) {
      const shown = slices.slice(0, LEGEND_MAX_ENTRIES - 1);
      const overflow = slices.slice(LEGEND_MAX_ENTRIES - 1);
      shown.push({
        label: `+${overflow.length} more`,
        count: overflow.reduce((sum, slice) => sum + slice.count, 0),
        color: 'rgba(255,255,255,0.22)',
        fraction: overflow.reduce((sum, slice) => sum + slice.fraction, 0),
      });
      return {
        slices: shown,
        // Names of the collapsed members — surfaced as a tooltip on the row.
        overflowNames: overflow.map((slice) => `${slice.label} (${slice.count})`).join(' · '),
      };
    }
    return { slices, overflowNames: '' };
  }, [campaigns.length, teamMembers, unassigned]);

  // Donut arc offsets — computed ONCE from the final slice list.
  const donutArcs = useMemo(() => {
    let acc = 0;
    return donutSlices.slices.map((slice) => {
      const offset = acc;
      acc += slice.fraction;
      return { ...slice, offset };
    });
  }, [donutSlices]);
  const donutTotal = campaigns.length;
  const compactLegend = donutSlices.slices.length > LEGEND_COMPACT_THRESHOLD;

  // Chart presentation toggle — Bars (default) or horizontal Funnel.
  const [pipelineView, setPipelineView] = useState<PipelineViewMode>(loadPipelineView);
  const setPersistedPipelineView = (next: PipelineViewMode) => {
    setPipelineView(next);
    try {
      localStorage.setItem(PIPELINE_VIEW_KEY, next);
    } catch {
      // Private mode / storage disabled — the in-session toggle still works.
    }
  };

  return <div className={`team-campaign-overview${compactLegend ? ' team-campaign-overview--dense' : ''}`}>
    <div className="team-campaign-snapshot">
      <h2 className="team-eyebrow">Campaigns</h2>
      {/* HERO total + breakdown sub-row (same vertical budget as the old
          three-equal-numbers grid): the big number is Total campaigns; the
          smaller ACTIVE | INACTIVE | UNASSIGNED line sits beneath it.
          Inactive = everything the board does NOT normalize to active
          (paused/stopped/archived) — mirrors normalizeCampaignStatus. */}
      <div className="team-campaign-numbers">
        <button type="button" onClick={onAll} className="team-campaign-total">
          <strong>{campaigns.length}</strong>
          <span>Total campaigns</span>
        </button>
        {/* Number ABOVE label (Manuel): vertical stack keeps each stat narrow,
            so the row never overflows into the next stat on smaller widths. */}
        <div className="team-campaign-substats">
          <button type="button" onClick={onActive}>
            <strong className="text-emerald-300">{active}</strong>
            <span>Total active</span>
          </button>
          <span className="team-campaign-substat-sep" aria-hidden="true" />
          <div className="team-campaign-substat">
            <strong>{campaigns.length - active}</strong>
            <span>Total inactive</span>
          </div>
          <span className="team-campaign-substat-sep" aria-hidden="true" />
          <button type="button" onClick={onUnassigned}>
            <strong className={unassigned ? 'text-amber-200' : ''}>{unassigned}</strong>
            <span>Unassigned</span>
          </button>
        </div>
      </div>
      {/* TWO-ROW weekly pill (Manuel's template), COVERAGE tone treatment:
          the pill stays neutral; only the "leads / week" accent text takes the
          runway tone (green = this week's enrichment covered, red = short /
          no credits). ROW 1 — TOTAL + accent + "Add credits" (only when
          coverage is short). ROW 2 — friendly coverage line. No status icons
          — accent IS the signal. */}
      {(() => {
        const needsCredits = runway.tone === 'risk' || runway.tone === 'empty';
        const toneClass =
          runway.tone === 'ok' ? 'team-weekly-pill-ok'
            : 'team-weekly-pill-risk';
        const weeks = runway.weeksRemaining === null
          ? null
          : `${runway.weeksRemaining} week${runway.weeksRemaining === 1 ? '' : 's'}`;
        const detailLabel = needsCredits && runway.creditsMissing > 0
          ? `Missing ${runway.creditsMissing.toLocaleString()} credits for weekly enrichment`
          : weeks === null
            ? 'No live weekly capacity'
            : `All under control. ${weeks} left`;
        return (
          <div className={`team-weekly-pill ${toneClass}`} title="Live campaigns' weekly lead capacity (paused, blocked, and finished campaigns excluded)">
            <span className="team-weekly-pill-row">
              <Icon icon="solar:bolt-bold" className="team-weekly-pill-icon" aria-hidden="true" />
              <span className="team-weekly-pill-value">{runway.weeklyLimit.toLocaleString()}</span>
              <span className="team-weekly-pill-unit">leads / week</span>
              {needsCredits && onAddCredits && (
                <button type="button" className="team-weekly-pill-cta" onClick={onAddCredits}>
                  Add credits
                </button>
              )}
            </span>
            <span className="team-weekly-pill-detail">{detailLabel}</span>
          </div>
        );
      })()}
    </div>
    <figure className="team-donut">
      <figcaption><span className="team-eyebrow">Assigned</span><span className="text-xs text-white/50">{donutTotal} campaign{donutTotal === 1 ? '' : 's'}</span></figcaption>
      <div className="team-donut-body">
        <svg viewBox="0 0 120 120" role="img" aria-label={`Campaigns per owner: ${teamMembers.map((m) => `${m.name} ${m.count}`).join(', ')}${unassigned ? `, Unassigned ${unassigned}` : ''}`}>
          <circle cx="60" cy="60" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="16" />
          {donutArcs.map((slice) => {
            const circumference = 2 * Math.PI * 42;
            const dash = slice.fraction * circumference;
            return (
              <circle
                key={slice.label}
                cx="60"
                cy="60"
                r="42"
                fill="none"
                stroke={slice.color}
                strokeWidth="16"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-slice.offset * circumference}
                transform="rotate(-90 60 60)"
              />
            );
          })}
          <text x="60" y="57" textAnchor="middle" className="team-donut-center-number">{donutTotal}</text>
          <text x="60" y="72" textAnchor="middle" className="team-donut-center-caption">TOTAL</text>
        </svg>
        <ul className={`team-donut-legend${compactLegend ? ' team-donut-legend--compact' : ''}`}>
          {donutSlices.slices.map((slice) => (
            <li
              key={slice.label}
              title={slice.label.startsWith('+') && donutSlices.overflowNames ? donutSlices.overflowNames : undefined}
            >
              <span className="team-donut-dot" style={{ backgroundColor: slice.color }} />
              <span className="team-donut-name">{slice.label}</span>
              <span className="team-donut-count">{slice.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </figure>
    <figure className="team-pipeline">
      <figcaption>
        <span className="team-eyebrow">Lead pipeline</span>
        {/* Chart-type toggle — same segmented pattern as the board's
            List/Compact switcher. Text labels keep it discoverable at 10px. */}
        <div className="team-pipeline-view-toggle" role="group" aria-label="Lead pipeline chart type">
          {([
            { key: 'bars', label: 'Bars', icon: 'bx:bar-chart-alt-2' },
            { key: 'funnel', label: 'Funnel', icon: 'bx:filter-alt' },
          ] as Array<{ key: PipelineViewMode; label: string; icon: string }>).map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={pipelineView === option.key}
              className={pipelineView === option.key ? 'is-active' : ''}
              onClick={() => setPersistedPipelineView(option.key)}
            >
              <Icon icon={option.icon} className="h-3 w-3" aria-hidden="true" />
              {option.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-white/50">{total.toLocaleString()} leads</span>
      </figcaption>
      {/* ONE chart component for BOTH renders — single data path, measurement,
          and scale. A bigger value can never render smaller in either mode. */}
      <PipelineChart stages={stages} variant={pipelineView} onStageClick={setActiveStage} />
    </figure>
    {/* Pipeline drill-down: the clicked status opens its lead list here. */}
    <PipelineStatusModal
      open={Boolean(activeStage)}
      stage={activeStage}
      campaigns={campaigns}
      userEmail={userEmail}
      sellerLabels={sellerLabels}
      onClose={() => setActiveStage(null)}
    />
  </div>;
};
